/**
 * Google Calendar Integration Hook
 *
 * Provides state and operations for Google Calendar connections and events.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/context/AuthContext';
import { useOrganization } from '@/context/OrganizationContext';
import {
  getConnections,
  type GoogleCalendarConnection,
} from '@/db/googleCalendar';
import {
  fetchCalendarEvents as dbFetchCalendarEvents,
  type CalendarEvent,
} from '@/db/calendarEvents';
import { handleError } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const GOOGLE_CALENDAR_SCOPES = 'https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/calendar.readonly';

interface GoogleCalendarInfo {
  id: string;
  summary: string;
  description: string | null;
  primary: boolean;
  backgroundColor: string | null;
  accessRole: string | null;
}

interface UseGoogleCalendarReturn {
  // State
  connections: GoogleCalendarConnection[];
  isConnected: boolean;
  isSyncing: boolean;
  isLoading: boolean;
  events: CalendarEvent[];
  calendars: GoogleCalendarInfo[];
  error: string | null;

  // Actions
  connect: () => void;
  disconnect: (connectionId: string) => Promise<void>;
  syncNow: () => Promise<void>;
  pushTaskToGoogle: (taskId: string) => Promise<void>;
  fetchEvents: (startTime: number, endTime: number) => Promise<CalendarEvent[]>;
  fetchCalendars: (connectionId: string) => Promise<void>;
  updateConnectionSettings: (
    connectionId: string,
    updates: {
      syncEnabled?: boolean;
      syncDirection?: 'bidirectional' | 'import_only' | 'export_only';
      selectedCalendarIds?: string[];
    }
  ) => Promise<void>;
  refreshConnections: () => Promise<void>;
}

export function useGoogleCalendar(): UseGoogleCalendarReturn {
  const { session } = useAuth();
  const { currentOrg } = useOrganization();
  const [connections, setConnections] = useState<GoogleCalendarConnection[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [calendars, setCalendars] = useState<GoogleCalendarInfo[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track message listener cleanup
  const messageListenerRef = useRef<((e: MessageEvent) => void) | null>(null);

  const isConnected = connections.some((c) => c.status === 'active');

  // Load connections on mount
  const loadConnections = useCallback(async () => {
    if (!session) return;
    setIsLoading(true);
    try {
      const conns = await getConnections();
      setConnections(conns);
    } catch (err) {
      handleError(err, { level: 'warning', message: 'Errore nel caricamento delle connessioni Google Calendar' });
      setError('Impossibile caricare le connessioni');
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  /**
   * Opens OAuth popup to connect Google Calendar
   */
  const connect = useCallback(() => {
    if (!GOOGLE_CLIENT_ID) {
      setError('Google Client ID non configurato');
      return;
    }

    const redirectUri = `${window.location.origin}/auth/google-calendar-callback`;
    const state = crypto.randomUUID();

    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', GOOGLE_CLIENT_ID);
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', GOOGLE_CALENDAR_SCOPES);
    authUrl.searchParams.set('access_type', 'offline');
    authUrl.searchParams.set('prompt', 'consent');
    authUrl.searchParams.set('state', state);

    // Store state for CSRF verification
    sessionStorage.setItem('gcal_oauth_state', state);

    const width = 500;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      authUrl.toString(),
      'google-calendar-auth',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
    );

    // Clean up previous listener
    if (messageListenerRef.current) {
      window.removeEventListener('message', messageListenerRef.current);
    }

    // Listen for the auth code from the popup
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data?.type || event.data.type !== 'google-calendar-auth') return;

      const { code, state: returnedState } = event.data;
      const savedState = sessionStorage.getItem('gcal_oauth_state');

      if (!code || returnedState !== savedState) {
        setError('Autenticazione non valida');
        return;
      }

      sessionStorage.removeItem('gcal_oauth_state');
      window.removeEventListener('message', handleMessage);
      messageListenerRef.current = null;

      try {
        // Exchange code for tokens via edge function
        const { data, error: fnError } = await supabase.functions.invoke('google-calendar-auth/exchange', {
          body: {
            code,
            orgId: currentOrg?.id || null,
          },
        });

        if (fnError) throw fnError;
        if (!data?.success) throw new Error(data?.error || 'Scambio token fallito');

        logger.log('[Google Calendar] Connected successfully');

        // Reload connections
        await loadConnections();
        setError(null);
      } catch (err) {
        handleError(err, { level: 'error', message: 'Errore nella connessione a Google Calendar' });
        setError('Impossibile connettersi a Google Calendar');
      }
    };

    messageListenerRef.current = handleMessage;
    window.addEventListener('message', handleMessage);

    // Cleanup after timeout or popup close
    const checkPopup = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(checkPopup);
        // Give a small delay for the message to arrive
        setTimeout(() => {
          if (messageListenerRef.current === handleMessage) {
            window.removeEventListener('message', handleMessage);
            messageListenerRef.current = null;
          }
        }, 2000);
      }
    }, 500);
  }, [currentOrg, loadConnections]);

  /**
   * Disconnect (revoke tokens and delete connection)
   */
  const disconnect = useCallback(
    async (connectionId: string) => {
      try {
        const { error: fnError } = await supabase.functions.invoke('google-calendar-auth/revoke', {
          body: { connectionId },
        });

        if (fnError) throw fnError;

        logger.log('[Google Calendar] Disconnected');
        setConnections((prev) => prev.filter((c) => c.id !== connectionId));
        setEvents([]);
        setCalendars([]);
        setError(null);
      } catch (err) {
        handleError(err, { level: 'error', message: 'Errore nella disconnessione da Google Calendar' });
        setError('Impossibile disconnettere Google Calendar');
      }
    },
    []
  );

  /**
   * Trigger manual sync
   */
  const syncNow = useCallback(async () => {
    const activeConnection = connections.find((c) => c.status === 'active');
    if (!activeConnection) return;

    setIsSyncing(true);
    setError(null);

    try {
      // Use full sync if never synced, otherwise incremental
      const endpoint = activeConnection.lastFullSyncAt
        ? 'google-calendar-sync/incremental-sync'
        : 'google-calendar-sync/full-sync';

      const { data, error: fnError } = await supabase.functions.invoke(endpoint, {
        body: { connectionId: activeConnection.id },
      });

      if (fnError) throw fnError;
      if (!data?.success) throw new Error(data?.error || 'Sincronizzazione fallita');

      logger.log('[Google Calendar] Sync completed');

      // Reload connections to get updated lastFullSyncAt
      await loadConnections();
    } catch (err) {
      handleError(err, { level: 'error', message: 'Errore nella sincronizzazione Google Calendar' });
      setError('Sincronizzazione fallita');
    } finally {
      setIsSyncing(false);
    }
  }, [connections, loadConnections]);

  /**
   * Push a task to Google Calendar
   */
  const pushTaskToGoogle = useCallback(
    async (taskId: string) => {
      const activeConnection = connections.find((c) => c.status === 'active');
      if (!activeConnection) {
        setError('Nessuna connessione Google Calendar attiva');
        return;
      }

      try {
        const { data, error: fnError } = await supabase.functions.invoke('google-calendar-sync/push-event', {
          body: {
            connectionId: activeConnection.id,
            taskId,
          },
        });

        if (fnError) throw fnError;
        if (!data?.success) throw new Error(data?.error || 'Invio evento fallito');

        logger.log('[Google Calendar] Task pushed to Google:', data.googleEventId);
      } catch (err) {
        handleError(err, { level: 'error', message: 'Errore nell\'invio del task a Google Calendar' });
        throw err;
      }
    },
    [connections]
  );

  /**
   * Fetch events for a date range
   */
  const fetchEvents = useCallback(
    async (startTime: number, endTime: number): Promise<CalendarEvent[]> => {
      try {
        const evts = await dbFetchCalendarEvents(startTime, endTime);
        setEvents(evts);
        return evts;
      } catch (err) {
        handleError(err, { level: 'warning', message: 'Errore nel caricamento degli eventi' });
        return [];
      }
    },
    []
  );

  /**
   * Fetch available calendars for a connection
   */
  const fetchCalendars = useCallback(
    async (connectionId: string) => {
      try {
        const { data: _data, error: fnError } = await supabase.functions.invoke('google-calendar-auth/calendars', {
          body: {},
          method: 'GET',
          headers: {},
        });

        // Since GET with body isn't ideal, use invoke with query param workaround
        // The edge function reads from URL params
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession) return;

        const res = await fetch(
          `${supabaseUrl}/functions/v1/google-calendar-auth/calendars?connectionId=${connectionId}`,
          {
            headers: {
              Authorization: `Bearer ${currentSession.access_token}`,
              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
          }
        );

        if (!res.ok) throw new Error('Failed to fetch calendars');
        const result = await res.json();

        if (fnError && !result.calendars) throw fnError;
        setCalendars(result.calendars || []);
      } catch (err) {
        handleError(err, { level: 'warning', message: 'Errore nel caricamento dei calendari' });
      }
    },
    []
  );

  /**
   * Update connection settings (sync direction, selected calendars, etc.)
   */
  const updateConnectionSettings = useCallback(
    async (
      connectionId: string,
      updates: {
        syncEnabled?: boolean;
        syncDirection?: 'bidirectional' | 'import_only' | 'export_only';
        selectedCalendarIds?: string[];
      }
    ) => {
      try {
        const { updateConnection } = await import('@/db/googleCalendar');
        const updated = await updateConnection(connectionId, updates);
        setConnections((prev) =>
          prev.map((c) => (c.id === connectionId ? updated : c))
        );
      } catch (err) {
        handleError(err, { level: 'error', message: 'Errore nell\'aggiornamento delle impostazioni' });
      }
    },
    []
  );

  return {
    connections,
    isConnected,
    isSyncing,
    isLoading,
    events,
    calendars,
    error,

    connect,
    disconnect,
    syncNow,
    pushTaskToGoogle,
    fetchEvents,
    fetchCalendars,
    updateConnectionSettings,
    refreshConnections: loadConnections,
  };
}
